/**
 * Lab iframe wrapper component with loading and error states
 * Supports URL synchronization for deep linking
 */

import { useState, useRef, useEffect, useCallback } from "react";
import { Box, CircularProgress, Alert, Button, Stack } from "@mui/material";
import { Refresh } from "@mui/icons-material";

interface LabFrameProps {
  url: string;
  title: string;
  onNavigate?: (url: string) => void;
}

export function LabFrame({ url, title, onNavigate }: LabFrameProps) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const lastKnownUrl = useRef<string>(url);

  const handleRetry = () => {
    setError(false);
    setLoading(true);
    window.location.reload();
  };

  // Normalize URL to just path + search + hash for comparison
  const normalizeUrl = useCallback((urlString: string): string => {
    try {
      const url = new URL(urlString, window.location.origin);
      return `${url.pathname}${url.search}${url.hash}`;
    } catch {
      return urlString;
    }
  }, []);

  // Check if we can access the iframe's location (same-origin)
  const getIframeLocation = useCallback((): string | null => {
    try {
      const iframe = iframeRef.current;
      if (iframe?.contentWindow?.location?.href) {
        // Return normalized path (not full URL) for consistent comparison
        return normalizeUrl(iframe.contentWindow.location.href);
      }
    } catch {
      // Cross-origin - can't access location
    }
    return null;
  }, [normalizeUrl]);

  // Handle iframe load event
  const handleLoad = useCallback(() => {
    setLoading(false);

    // Try to get the iframe's current URL
    const currentUrl = getIframeLocation();
    if (currentUrl && currentUrl !== lastKnownUrl.current) {
      lastKnownUrl.current = currentUrl;
      onNavigate?.(currentUrl);
    }
  }, [getIframeLocation, onNavigate]);

  // Poll for URL changes (for same-origin iframes with SPA navigation)
  useEffect(() => {
    if (!onNavigate) return;

    const pollInterval = setInterval(() => {
      const currentUrl = getIframeLocation();
      if (currentUrl && currentUrl !== lastKnownUrl.current) {
        lastKnownUrl.current = currentUrl;
        onNavigate(currentUrl);
      }
    }, 500);

    return () => clearInterval(pollInterval);
  }, [getIframeLocation, onNavigate]);

  // Listen for postMessage from iframe (for cross-origin support)
  useEffect(() => {
    if (!onNavigate) return;

    const handleMessage = (event: MessageEvent) => {
      // Verify the message is from our iframe
      if (iframeRef.current?.contentWindow !== event.source) return;

      // Handle navigation messages from child frames
      if (event.data?.type === "LAB_NAVIGATE" && event.data?.url) {
        const normalizedUrl = normalizeUrl(event.data.url);
        lastKnownUrl.current = normalizedUrl;
        onNavigate(normalizedUrl);
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [onNavigate, normalizeUrl]);

  // Update lastKnownUrl when the url prop changes (parent-driven navigation)
  useEffect(() => {
    lastKnownUrl.current = normalizeUrl(url);
  }, [url, normalizeUrl]);

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        position: "relative",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {loading && (
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            zIndex: 1,
          }}
        >
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            zIndex: 1,
            width: "90%",
            maxWidth: 500,
          }}
        >
          <Stack spacing={2}>
            <Alert severity="error">
              Failed to load {title}. The lab may be unavailable or the URL may be incorrect.
            </Alert>
            <Button variant="contained" startIcon={<Refresh />} onClick={handleRetry}>
              Retry
            </Button>
          </Stack>
        </Box>
      )}

      <iframe
        ref={iframeRef}
        src={url}
        title={title}
        style={{
          width: "100%",
          height: "100%",
          border: "none",
          display: error ? "none" : "block",
        }}
        sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox allow-downloads"
        onLoad={handleLoad}
        onError={() => {
          setLoading(false);
          setError(true);
        }}
      />
    </Box>
  );
}
